import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { useStorefrontTenantSettings } from './useStorefrontTenantSettings'
import type {
  TenantSettingsResponse,
  WarehouseStrategy,
} from '@/lib/api/controlPlaneClient'
import { PriceListEditorModal } from './PriceListEditorModal'
import { StockSourcesEditorModal } from './StockSourcesEditorModal'
import { SettingRow, Divider } from './SettingRow'

export function PricingSettings() {
  const {
    settings,
    isLoading,
    isError,
    updateAsync,
    isUpdating,
    queryKey,
  } = useStorefrontTenantSettings()
  const queryClient = useQueryClient()
  const [priceListModalOpen, setPriceListModalOpen] = useState(false)
  const [stockSourcesModalOpen, setStockSourcesModalOpen] = useState(false)

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load pricing settings')
    }
  }, [isError])

  const stockSourcesLabel =
    settings?.warehouse_strategy === 'all'
      ? 'All warehouses'
      : settings?.warehouse_strategy === 'selected'
        ? `${settings.warehouses.length} warehouses selected`
        : null

  const handleSavePriceList = async (priceList: string) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) => (old ? { ...old, selling_price_list: priceList } : old),
    )

    try {
      await updateAsync({ selling_price_list: priceList })
      toast('Price list updated')
      setPriceListModalOpen(false)
    } catch (e) {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
      throw e
    }
  }

  const handleSaveStockSources = async (
    strategy: WarehouseStrategy,
    warehouses: string[],
  ) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) =>
        old ? { ...old, warehouse_strategy: strategy, warehouses } : old,
    )

    try {
      await updateAsync({ warehouse_strategy: strategy, warehouses })
      toast('Stock sources updated')
      setStockSourcesModalOpen(false)
    } catch (e) {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
      throw e
    }
  }

  const handleToggleHideOutOfStock = async (checked: boolean) => {
    const previousData = queryClient.getQueryData<TenantSettingsResponse>(
      queryKey,
    )

    queryClient.setQueryData<TenantSettingsResponse | undefined>(
      queryKey,
      (old) => (old ? { ...old, hide_out_of_stock: checked ? 1 : 0 } : old),
    )

    try {
      await updateAsync({ hide_out_of_stock: checked ? 1 : 0 })
      toast('Setting saved.')
    } catch {
      queryClient.setQueryData(queryKey, previousData)
      toast.error('Failed to save. Please try again.')
    }
  }

  return (
    <>
      <Card className="rounded-2xl border-0">
        <CardContent className="p-0">
          {/* Price List */}
          <SettingRow
            label="Price List"
            clickable
            onClick={() => setPriceListModalOpen(true)}
          >
            {isLoading ? (
              <Skeleton className="w-32 h-4 rounded-md" />
            ) : (
              <span className="text-sm text-muted-foreground">
                {settings?.selling_price_list ?? '—'}
              </span>
            )}
          </SettingRow>

          <Divider />

          {/* Stock Sources */}
          <SettingRow
            label="Stock Sources"
            clickable
            onClick={() => setStockSourcesModalOpen(true)}
          >
            {isLoading ? (
              <Skeleton className="w-32 h-4 rounded-md" />
            ) : (
              <span className="text-sm text-muted-foreground">
                {stockSourcesLabel ?? '—'}
              </span>
            )}
          </SettingRow>

          <Divider />

          {/* Hide Out-of-Stock */}
          <SettingRow label="Hide Out-of-Stock">
            {isLoading ? (
              <Skeleton className="w-9 h-5 rounded-full" />
            ) : (
              <Switch
                checked={!!settings?.hide_out_of_stock}
                onCheckedChange={handleToggleHideOutOfStock}
                disabled={isUpdating}
                aria-label="Hide out-of-stock items"
              />
            )}
          </SettingRow>

          <Divider />

          {/* Default Personalization Item */}
          <SettingRow label="Personalization Item">
            {isLoading ? (
              <Skeleton className="w-32 h-4 rounded-md" />
            ) : (
              <span className="text-sm text-muted-foreground">
                {settings?.default_personalization_item || 'Auto-created'}
              </span>
            )}
          </SettingRow>
        </CardContent>
      </Card>

      <PriceListEditorModal
        open={priceListModalOpen}
        onOpenChange={setPriceListModalOpen}
        currentPriceList={settings?.selling_price_list ?? null}
        onSave={handleSavePriceList}
        isSaving={isUpdating}
      />

      <StockSourcesEditorModal
        open={stockSourcesModalOpen}
        onOpenChange={setStockSourcesModalOpen}
        currentStrategy={settings?.warehouse_strategy ?? null}
        currentWarehouses={settings?.warehouses ?? []}
        onSave={handleSaveStockSources}
        isSaving={isUpdating}
      />
    </>
  )
}
